import { z } from 'zod';
import { Cents, CurrencyCode, TaxRateBp } from './invoicing.js';
/**
 * Client estimates (docs/adr/0088-mcp-server-and-estimates.md): a quote to one of the workspace's clients, with lines.
 * Under the Invoicing feature and its levels (docs/adr/0054): `status` sees number, title, client, status and dates;
 * `manage` sees amounts and writes. Money is whole cents and totals are computed by the server, never sent by a caller.
 */
export const EstimateStatus = z.enum(['draft', 'sent', 'accepted', 'declined', 'expired']);
export const EstimateVia = z.enum(['web', 'mobile', 'import', 'mcp']);
/** One line: quantity may have two decimals; `hours` is the work it stands for, when it is priced by time. */
export const EstimateLineInput = z.object({
    description: z.string().trim().min(1).max(500),
    quantity: z.number().min(0).max(100_000).refine((n) => Math.abs(n * 100 - Math.round(n * 100)) < 1e-6, 'At most two decimals'),
    unitAmountCents: Cents,
    hours: z.number().min(0).max(100_000).nullable().optional(),
});
export const EstimateLine = z.object({
    id: z.uuid(),
    position: z.number().int(),
    description: z.string(),
    quantity: z.number(),
    unitAmountCents: z.number().int(),
    amountCents: z.number().int(),
    hours: z.number().nullable(),
});
export const EstimateSummary = z.object({
    id: z.uuid(),
    /** "E-0001": the workspace's own running number. */
    number: z.string(),
    title: z.string(),
    status: EstimateStatus,
    clientId: z.uuid(),
    clientName: z.string(),
    projectId: z.uuid().nullable(),
    currency: CurrencyCode,
    issueDate: z.iso.date(),
    validUntil: z.iso.date().nullable(),
    /** Null for people who have the status level only (they never see an amount). */
    totalCents: z.number().int().nullable(),
    createdVia: EstimateVia,
    updatedAt: z.string(),
});
export const Estimate = EstimateSummary.extend({
    notes: z.string(),
    taxRateBp: TaxRateBp,
    taxLabel: z.string(),
    subtotalCents: z.number().int().nullable(),
    taxCents: z.number().int().nullable(),
    /** Empty for people who have the status level only. */
    lines: z.array(EstimateLine),
    sentAt: z.string().nullable(),
    decidedAt: z.string().nullable(),
    createdAt: z.string(),
});
export const EstimateList = z.object({ items: z.array(EstimateSummary) });
export const EstimateListQuery = z.object({ status: EstimateStatus.optional(), clientId: z.uuid().optional() });
export const CreateEstimateInput = z.object({
    clientId: z.uuid(),
    projectId: z.uuid().nullable().optional(),
    title: z.string().trim().min(1).max(200),
    /** Leave out for the client's invoicing currency, else the workspace's. */
    currency: CurrencyCode.optional(),
    issueDate: z.iso.date().optional(),
    validUntil: z.iso.date().nullable().optional(),
    notes: z.string().max(5000).optional(),
    taxRateBp: TaxRateBp.optional(),
    taxLabel: z.string().trim().max(20).optional(),
    status: EstimateStatus.optional(),
    lines: z.array(EstimateLineInput).max(200).default([]),
    /** Where it came from (the MCP server sets `mcp`); the app uses `web`. */
    via: EstimateVia.optional(),
});
/** `lines`, when given, replaces all lines (and the totals follow). */
export const UpdateEstimateInput = z.object({
    title: z.string().trim().min(1).max(200).optional(),
    projectId: z.uuid().nullable().optional(),
    status: EstimateStatus.optional(),
    validUntil: z.iso.date().nullable().optional(),
    notes: z.string().max(5000).optional(),
    taxRateBp: TaxRateBp.optional(),
    taxLabel: z.string().trim().max(20).optional(),
    lines: z.array(EstimateLineInput).max(200).optional(),
});
/** "E-0007" from the running number. */
export const estimateNumber = (n) => `E-${String(n).padStart(4, '0')}`;
/** What one line comes to, in cents: quantity (two decimals) times the unit amount, rounded half up. */
export const estimateLineCents = (quantity, unitAmountCents) => Math.round((Math.round(quantity * 100) * unitAmountCents) / 100);
/** Subtotal, tax and total for a set of lines. Tax is rounded half up on the subtotal. */
export function estimateTotals(lines, taxRateBp) {
    const subtotalCents = lines.reduce((sum, l) => sum + estimateLineCents(l.quantity, l.unitAmountCents), 0);
    const taxCents = Math.round((subtotalCents * taxRateBp) / 10_000);
    return { subtotalCents, taxCents, totalCents: subtotalCents + taxCents };
}
/** Allowed moves: a decided estimate is never put back to draft. */
export function estimateStatusMoveAllowed(from, to) {
    if (from === to)
        return true;
    if (to === 'draft')
        return from === 'sent';
    return true;
}
//# sourceMappingURL=estimates.js.map